// Beranda per pohon aktif (Figma "Fola+ Home" & "Fola+ Home (with banner)" + PRD 12.3): banner, platform
// partner, promo, rekomendasi, strip promo, lalu ringkasan pohon (saldo, tier, pendapatan, referral).
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import { FolaLogo } from '../components/FolaLogo';
import { ChevronRightIcon, CloseIcon, PlayIcon, WhatsAppGlyph } from '../components/icons';
import { Alert, ButtonLink, Card, Empty, ErrorState, Loading, Row } from '../components/ui';
import { useActiveTree } from '../lib/active-tree';
import { api, type ResponseOf } from '../lib/api';
import { canUpgrade, useCatalog } from '../lib/catalog';
import { WITHDRAW_ENABLED } from '../lib/flags';
import { formatDate, formatRupiah } from '../lib/format';
import { platformLogo } from '../lib/platforms';
import { POINTS_TOTAL } from '../lib/points';

type Dashboard = ResponseOf<'dashboard'>;

export function HomePage() {
  const { activeTree, isLoading, error, refetch } = useActiveTree();
  const dashboard = useQuery({
    queryKey: ['dashboard', activeTree?.id],
    queryFn: () => api('dashboard', { params: { id: activeTree!.id } }),
    enabled: Boolean(activeTree),
  });

  if (isLoading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  if (!activeTree) return <Empty title="Belum ada pohon">Buat pohon di menu Kelola pohon.</Empty>;
  if (dashboard.isPending) return <Loading />;
  if (dashboard.error) return <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />;

  const data = dashboard.data;

  return (
    <div className="space-y-5">
      <h1 className="sr-only">Beranda {data.tree.name}</h1>

      {data.tree.status === 'PENDING_PAYMENT' ? (
        <Alert tone="warning">
          Pohon ini belum aktif.{' '}
          <Link to={`/my-trees/${data.tree.id}/pay`} className="font-semibold underline">
            Selesaikan pembayaran paket
          </Link>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
        <div className="md:col-span-2">
          <HeroBanner />
        </div>
        <div className="md:hidden">
          <PlatformHub />
        </div>
        <GreetingCard data={data} />
      </div>

      <MerchantRow />

      <SpecialPromoSection />
      <div className="md:hidden">
        <RecommendationSection />
      </div>

      <div className="md:hidden">
        <PromoStrip />
      </div>

      <section className="space-y-3">
        <SectionHeader title="Ringkasan Pohon" to="/wallet" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <BalanceCard data={data} />
          <TierCard data={data} />
          <IncomeCard data={data} />
          <ReferralCard code={data.tree.referralCode} />
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ title, to, subtitle }: { title: string; to?: string; subtitle?: string }) {
  return (
    <div className="flex items-end justify-between gap-2">
      <div>
        <h2 className="text-[13px] font-bold text-fola-ink">{title}</h2>
        {subtitle ? <p className="text-[10px] text-slate-500">{subtitle}</p> : null}
      </div>
      {to ? (
        <Link
          to={to}
          className="flex items-center text-[11px] font-semibold text-fola-indigo hover:underline"
        >
          Lihat Semua <ChevronRightIcon className="size-3" strokeWidth={2.4} />
        </Link>
      ) : null}
    </div>
  );
}

type Banner =
  /** Banner jadi dari desain: teks dan tombol sudah ada di gambar. */
  | { kind: 'image'; image: string; imageDesktop: string; alt: string }
  | { kind: 'text'; tag: string; title: string; sub: string; cta: string; bg: string };

const BANNERS: Banner[] = [
  {
    kind: 'image',
    image: '/assets/home/banner-folaplay.jpg', // mobile, 1026×492
    imageDesktop: '/assets/home/banner-folaplay-desktop.jpg', // ≥768px, 1780×560 (Figma Home Pages)
    alt: 'Folaplay: Pilih Playboy, Balas Takdir. Watch only in Folaplay.',
  },
  {
    kind: 'text',
    tag: 'Special Offer',
    title: 'Double Cashback TikTok Shop 50%',
    sub: 'Belanja & dapatkan komisi instan',
    cta: 'Belanja',
    bg: 'from-[#1e1b4b] via-[#5b21b6] to-[#db2777]',
  },
];

function HeroBanner() {
  const [slide, setSlide] = useState(0);
  const current = BANNERS[slide] ?? BANNERS[0]!;

  return (
    // Rasio = rasio aset banner (mobile 1026×492, desktop 1780×560), agar teks di gambar tidak terpotong.
    <div
      className={`relative flex aspect-[1026/492] w-full md:aspect-[1780/560] flex-col justify-between overflow-hidden rounded-2xl text-white ${
        current.kind === 'text' ? `bg-gradient-to-br p-4 md:p-7 ${current.bg}` : 'bg-slate-900'
      }`}
    >
      {current.kind === 'image' ? (
        // Tombol "PLAY NOW" bagian dari gambar; tujuannya belum didefinisikan di Figma, jadi tidak bisa diklik.
        <picture>
          {/* md (768px) = breakpoint desktop Tailwind; browser hanya mengunduh satu gambar. */}
          <source media="(min-width: 768px)" srcSet={current.imageDesktop} />
          <img src={current.image} alt={current.alt} className="absolute inset-0 size-full object-cover" />
        </picture>
      ) : (
        <>
          <span className="inline-flex w-fit items-center gap-1 rounded-md bg-black/25 px-2 py-0.5 text-[10px] font-bold md:px-3 md:py-1 md:text-sm">
            <PlayIcon className="size-3 fill-current" /> {current.tag}
          </span>
          <div>
            <h2 className="line-clamp-2 max-w-[70%] font-serif text-xl leading-tight font-bold italic drop-shadow md:max-w-[60%] md:text-4xl">
              {current.title}
            </h2>
            <p className="mt-0.5 text-[9px] font-semibold tracking-wider text-white/80 uppercase md:mt-2 md:text-xs">
              {current.sub}
            </p>
            {/* Tujuan CTA banner belum didefinisikan di Figma: nonaktif sampai ada keputusan PM. */}
            <button
              type="button"
              disabled
              className="mt-2 inline-flex cursor-not-allowed items-center gap-1 rounded-full bg-fola-indigo px-3 py-1 text-[9px] font-bold tracking-wide text-white uppercase opacity-60 md:mt-4 md:gap-2 md:px-6 md:py-2 md:text-sm"
            >
              <PlayIcon className="size-2.5 fill-current md:size-3.5" /> {current.cta}
            </button>
          </div>
        </>
      )}
      <div className="absolute inset-x-0 bottom-2 flex justify-center gap-1">
        {BANNERS.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setSlide(i)}
            className={`h-1 rounded-full transition-all ${i === slide ? 'w-4 bg-white' : 'w-1 bg-white/50 hover:bg-white/80'}`}
            aria-label={`Banner ${i + 1}`}
            aria-current={i === slide}
          />
        ))}
      </div>
    </div>
  );
}

/** Warna merek untuk tile platform tanpa logo sama sekali. */
const PLATFORM_COLOR: Record<string, string> = {
  TIKTOK: 'text-black',
  SHOPEE: 'text-[#ee4d2d]',
  TOKOPEDIA: 'text-[#03ac0e]',
  BLIBLI: 'text-[#0095da]',
  LAZADA: 'text-[#f57224]',
};

function PlatformHub() {
  const platforms = useQuery({ queryKey: ['platforms'], queryFn: () => api('platforms') });
  return (
    <section aria-label="Platform partner" className="rounded-2xl bg-[#efeafe] p-2.5 lg:h-full">
      {platforms.isPending ? (
        <div className="h-[52px]" />
      ) : platforms.error || platforms.data.length === 0 ? (
        <p className="px-2 py-4 text-center text-[11px] text-slate-500">Platform partner belum tersedia.</p>
      ) : (
        <ul className="no-scrollbar flex gap-2.5 overflow-x-auto lg:flex-wrap">
          {platforms.data.map((p) => (
            <li key={p.id} className="shrink-0">
              <span
                title={p.name}
                aria-label={p.name}
                className="flex size-[52px] items-center justify-center overflow-hidden rounded-xl bg-white shadow-sm"
              >
                {platformLogo(p) ? (
                  <img src={platformLogo(p)!} alt="" className="size-full object-cover" />
                ) : (
                  <span
                    className={`text-xl font-black ${PLATFORM_COLOR[p.code.toUpperCase()] ?? 'text-fola-indigo'}`}
                  >
                    {p.name.charAt(0)}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Kartu sapaan desktop (Figma Home desktop): nama, level, poin, saldo, tombol ke Access. */
function GreetingCard({ data }: { data: Dashboard }) {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('me') });
  const first = me.data?.fullName.split(' ')[0] ?? '';
  return (
    <section className="hidden flex-col rounded-2xl bg-gold-gradient p-5 text-white md:flex">
      <div className="flex items-start justify-between gap-2">
        <h2 className="truncate text-lg font-bold">Halo, {first}</h2>
        <span className="shrink-0 rounded-full bg-white/85 px-2.5 py-0.5 text-[10px] font-semibold text-[#9a7425]">
          Member Gold
        </span>
      </div>
      <p className="mt-5 text-[11px] text-white/85">Poin saya</p>
      <p className="text-[28px] leading-tight font-bold tabular-nums">{POINTS_TOTAL} poin</p>
      <p className="mt-3 text-xs text-white/90">Saldo {formatRupiah(data.balances.redeem)}</p>
      <Link
        to="/access"
        className="mt-auto inline-flex w-fit items-center gap-1 rounded-md bg-[#6b4f1d] px-3.5 py-2 text-[11px] font-semibold text-white transition hover:bg-[#5a4218]"
      >
        Buka Access →
      </Link>
    </section>
  );
}

/** Warna tombol merchant desktop (Figma "Merchant pilihan"). */
const MERCHANT_BG: Record<string, string> = {
  TIKTOK: 'bg-black',
  SHOPEE: 'bg-[#ee4d2d]',
  TOKOPEDIA: 'bg-[#42b549]',
  BLIBLI: 'bg-[#1c6ef2]',
  LAZADA: 'bg-[#2d1fe0]',
};

function MerchantRow() {
  const platforms = useQuery({ queryKey: ['platforms'], queryFn: () => api('platforms') });
  if (!platforms.data || platforms.data.length === 0) return null;
  return (
    <section className="hidden space-y-3 md:block">
      <h2 className="text-lg font-bold text-fola-ink">Merchant pilihan</h2>
      <ul className="grid grid-cols-5 gap-4">
        {platforms.data.map((p) => {
          const code = p.code.toUpperCase();
          const logo = platformLogo(p);
          return (
            <li key={p.id}>
              <div
                className={`flex h-12 items-center justify-center gap-2.5 rounded-lg px-3 text-sm font-semibold text-white ${MERCHANT_BG[code] ?? 'bg-fola-indigo'}`}
              >
                {logo ? (
                  <img src={logo} alt="" className="size-7 shrink-0 rounded-md object-cover" />
                ) : (
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-white/20 text-sm font-black">
                    {p.name.charAt(0)}
                  </span>
                )}
                <span className="truncate">{p.name}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function SpecialPromoSection() {
  const promos = [
    {
      kicker: 'Diskon s.d.',
      value: '50%',
      category: 'Fashion & Kecantikan',
      headline: 'Diskon hingga 50%',
      sub: 'Pilihan baru untuk gayamu',
      tone: 'text-fola-indigo',
      bg: 'bg-[#fbeef3]',
      fade: 'from-[#fbeef3]',
      image: '/assets/home/promo-fashion.png',
    },
    {
      kicker: 'Cashback s.d.',
      value: '100rb',
      category: 'Hotel & Travel',
      headline: 'Cashback 100rb',
      sub: 'Rencanakan perjalanan berikutnya',
      tone: 'text-[#a8781f]',
      bg: 'bg-[#eef1f8]',
      fade: 'from-[#eef1f8]',
      image: '/assets/home/promo-travel.png',
    },
  ];
  return (
    <section className="space-y-2.5 md:space-y-3">
      <div className="md:hidden">
        <SectionHeader title="Promo Spesial Untuk Kamu" to="/shop" />
      </div>
      <div className="hidden items-end justify-between md:flex">
        <h2 className="text-lg font-bold text-fola-ink">Promo spesial untuk kamu</h2>
        <Link to="/shop" className="text-xs font-semibold text-fola-indigo hover:underline">
          Lihat semua →
        </Link>
      </div>
      {/* Desktop: kartu lebar dengan judul promo */}
      <div className="hidden grid-cols-2 gap-5 md:grid">
        {promos.map((p) => (
          <div
            key={p.category}
            className={`relative flex h-28 items-center overflow-hidden rounded-2xl px-6 ${p.bg}`}
          >
            <img
              src={p.image}
              alt=""
              className="absolute inset-y-0 right-0 h-full w-[45%] object-cover object-center"
            />
            <span
              aria-hidden
              className={`absolute inset-y-0 left-[55%] w-12 bg-gradient-to-r to-transparent ${p.fade}`}
            />
            <span className="relative z-10">
              <span className="block text-[11px] text-slate-500">{p.category}</span>
              <span className={`mt-1 block text-[22px] leading-tight font-bold ${p.tone}`}>{p.headline}</span>
              <span className="mt-1 block text-[11px] text-slate-500">{p.sub}</span>
            </span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2.5 md:hidden">
        {promos.map((p) => (
          <div
            key={p.category}
            className={`relative flex h-[4.5rem] items-center overflow-hidden rounded-xl px-3 ${p.bg}`}
          >
            <img
              src={p.image}
              alt=""
              className="absolute inset-y-0 right-0 h-full w-[62%] object-cover object-center"
            />
            <span
              aria-hidden
              className={`absolute inset-y-0 left-[38%] w-8 bg-gradient-to-r to-transparent ${p.fade}`}
            />
            <span className="relative z-10">
              <span className="block text-[8px] text-slate-500">{p.kicker}</span>
              <span className="block text-xl leading-tight font-black text-fola-ink">{p.value}</span>
              <span className="block text-[8px] text-slate-500">{p.category}</span>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function RecommendationSection() {
  const categories = [
    { name: 'Kuliner', image: '/assets/home/rec-kuliner.png' },
    { name: 'Travel', image: '/assets/home/rec-travel.png' },
    { name: 'Elektronik', image: '/assets/home/rec-elektronik.png' },
    { name: 'Hiburan', image: '/assets/home/rec-hiburan.png' },
  ];
  return (
    <section className="space-y-2.5">
      <SectionHeader title="Rekomendasi Untuk Kamu" to="/lifestyle" />
      <div className="grid grid-cols-4 gap-2">
        {categories.map((c) => (
          <div key={c.name} className="text-center">
            <img
              src={c.image}
              alt=""
              width={78}
              height={110}
              className="aspect-[78/110] w-full rounded-lg bg-slate-100 object-cover"
            />
            <span className="mt-1 block text-[10px] font-semibold text-fola-ink">{c.name}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

const PROMO_STRIP_KEY = 'fola.home.promo-strip-dismissed';

/** Strip promo merah (Figma "Fola+ Home (with banner)"); bisa ditutup, diingat per sesi browser. */
function PromoStrip() {
  const [hidden, setHidden] = useState(() => {
    try {
      return sessionStorage.getItem(PROMO_STRIP_KEY) === '1';
    } catch {
      return false;
    }
  });
  if (hidden) return null;
  return (
    <div className="relative -mx-4 flex items-center justify-center gap-1.5 bg-[#c62828] px-8 py-2.5 text-[10px] font-medium text-white sm:-mx-6 md:mx-0 md:rounded-xl">
      <span>Dapatkan promo dan penawaran menarik lainnya hanya di</span>
      <FolaLogo variant="white" size="sm" className="!h-4" />
      <button
        type="button"
        onClick={() => {
          setHidden(true);
          try {
            sessionStorage.setItem(PROMO_STRIP_KEY, '1');
          } catch {
            // Penyimpanan diblokir: cukup sembunyikan untuk tampilan ini.
          }
        }}
        className="absolute -top-2 right-2 flex size-5 items-center justify-center rounded-full bg-white text-fola-ink shadow"
        aria-label="Tutup promo"
      >
        <CloseIcon className="size-3" strokeWidth={2.6} />
      </button>
    </div>
  );
}

function BalanceCard({ data }: { data: Dashboard }) {
  return (
    <section className="hero-orb rounded-2xl bg-fola-hero p-5 text-white shadow-fola">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-white/85">Saldo Siap Tarik (Redeem)</p>
        <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-[10px] font-semibold">1 : 1 Rupiah</span>
      </div>

      <div className="my-3 flex items-end justify-between gap-2">
        <p className="text-3xl font-bold tracking-tight tabular-nums" data-testid="saldo-redeem">
          {formatRupiah(data.balances.redeem)}
        </p>
        {WITHDRAW_ENABLED ? (
          <Link
            to="/wallet/withdraw"
            className="inline-flex min-h-10 items-center justify-center rounded-full bg-white px-5 text-xs font-bold text-fola-indigo shadow-sm transition hover:bg-slate-50"
          >
            Cairkan
          </Link>
        ) : (
          <button
            type="button"
            disabled
            className="inline-flex min-h-10 cursor-not-allowed items-center justify-center rounded-full bg-white px-5 text-xs font-bold text-fola-indigo opacity-60 shadow-sm"
          >
            Cairkan
          </button>
        )}
      </div>

      <div className="mt-4 flex justify-between border-t border-white/20 pt-3 text-xs">
        <div>
          <p className="text-[11px] text-white/85">Saldo Tampungan (Pending)</p>
          <p className="font-bold tabular-nums mt-0.5 text-sm" data-testid="saldo-tampungan">
            {formatRupiah(data.balances.pending)}
          </p>
        </div>
        <p className="self-end text-[11px] text-white/85">{data.pendingItems} order menunggu</p>
      </div>

      {data.balances.locked !== '0' ? (
        <p className="mt-2 text-[11px] font-medium text-white/85">
          Dalam proses pencairan: {formatRupiah(data.balances.locked)}
        </p>
      ) : null}
    </section>
  );
}

function TierCard({ data }: { data: Dashboard }) {
  const catalog = useCatalog();
  const tiers = catalog.data?.tiers ?? [];
  const name = (code: string | null) =>
    code ? (tiers.find((t) => t.code === code)?.displayName ?? code) : '—';
  const current = tiers.find((t) => t.code === data.tier.tierCode);
  const next = tiers.find((t) => t.code === data.tier.nextTierCode);

  let progress = 100;
  if (current && next) {
    const span = BigInt(next.minGroupGmv) - BigInt(current.minGroupGmv);
    const done = BigInt(data.tier.groupGmv) - BigInt(current.minGroupGmv);
    progress = span > 0n ? Math.max(0, Math.min(100, Number((done * 100n) / span))) : 100;
  }

  if (data.tree.classCode === 'SHOPPER') {
    return (
      <Card className="p-5">
        <p className="text-xs font-semibold text-slate-500">Tier saat ini</p>
        <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="tier-name">
          Tanpa tier
        </p>
        <p className="mt-2 text-xs text-slate-600">
          Kelas Shopper tidak memakai tier. GMV belanja periode ini{' '}
          <span className="font-bold tabular-nums text-slate-900">{formatRupiah(data.tier.ownGmv)}</span>
        </p>
        {canUpgrade(data.tree, catalog.data?.shopperUpgradeAllowed) ? (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <p className="text-xs text-slate-600">
              Upgrade ke General atau PNM untuk mendapat tier, cashback tambahan, dan insentif downline 2
              level.
            </p>
            <ButtonLink to={`/my-trees/${data.tree.id}/upgrade`} className="mt-3 w-full text-xs">
              Upgrade Pohon
            </ButtonLink>
          </div>
        ) : null}
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-500">Tier saat ini</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="tier-name">
            {name(data.tier.tierCode)}
          </p>
        </div>
        <span className="rounded-full bg-fola-lavender px-3 py-1 text-[11px] font-semibold text-fola-indigo">
          Sisa {data.tier.remainingDays} hari
        </span>
      </div>

      <p className="mt-3 text-xs text-slate-600">
        GMV grup periode ini{' '}
        <span className="font-bold tabular-nums text-slate-900">{formatRupiah(data.tier.groupGmv)}</span>
      </p>

      <div
        className="mt-2.5 h-2.5 overflow-hidden rounded-full bg-slate-100"
        role="progressbar"
        aria-label="Progres ke tier berikutnya"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
      >
        <div className="h-full rounded-full bg-fola-gradient" style={{ width: `${progress}%` }} />
      </div>

      <p className="mt-2 text-xs font-medium text-slate-600">
        {next && data.tier.remainingToNextTier
          ? `${formatRupiah(data.tier.remainingToNextTier)} lagi menuju ${next.displayName}`
          : 'Tier tertinggi tercapai! 🎉'}
      </p>

      <p className="mt-2 text-[10px] text-slate-400">
        Periode {formatDate(data.tier.periodStartsAt)} – {formatDate(data.tier.periodEndsAt)}
      </p>
    </Card>
  );
}

function IncomeCard({ data }: { data: Dashboard }) {
  const groups: { label: string; components: string[] }[] = [
    { label: 'Cashback Belanja', components: ['CASHBACK', 'ADDITIONAL_CASHBACK'] },
    { label: 'Insentif Direct (L1)', components: ['DIRECT_NON_MEMBER', 'DIRECT_RECRUITEE'] },
    { label: 'Insentif Indirect (L2)', components: ['INDIRECT_RECRUITEE'] },
    { label: 'Membership Commission', components: ['MEMBERSHIP_DIRECT', 'MEMBERSHIP_INDIRECT'] },
  ];
  const sum = (components: string[]) =>
    components.reduce((total, c) => total + BigInt(data.income.byComponent[c] ?? '0'), 0n);

  return (
    <Card className="p-5">
      <h2 className="text-sm font-bold text-slate-900">Pendapatan Periode Ini</h2>
      <dl className="mt-3 divide-y divide-slate-100">
        {groups.map((g) => (
          <Row key={g.label} label={<span className="text-xs text-slate-600">{g.label}</span>}>
            <span className="text-xs font-semibold text-slate-900">{formatRupiah(sum(g.components))}</span>
          </Row>
        ))}
        <Row label={<span className="font-bold text-slate-900 text-sm">Total Akumulasi</span>} strong>
          <span className="text-base font-bold text-fola-indigo">{formatRupiah(data.income.total)}</span>
        </Row>
      </dl>
    </Card>
  );
}

function ReferralCard({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/register?ref=${encodeURIComponent(code)}`;
  const shareText = `Gabung Fola+ dan dapatkan cashback belanja! Daftar dengan kode referral ${code}: ${link}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Card className="p-5">
      <p className="text-xs font-semibold text-slate-500">Kode Referral Kamu</p>
      <p
        className="mt-1 font-mono text-3xl font-bold tracking-widest text-fola-ink"
        data-testid="referral-code"
      >
        {code}
      </p>
      <div className="mt-4 grid grid-cols-2 gap-2.5">
        <button
          type="button"
          onClick={() => void copy()}
          className="min-h-11 rounded-full border border-slate-200 text-xs font-bold text-fola-ink transition hover:bg-slate-50"
        >
          <span aria-live="polite">{copied ? 'Tersalin ✓' : 'Salin Kode'}</span>
        </button>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#20ba59] px-4 text-xs font-bold text-white shadow-sm transition active:scale-95"
        >
          <WhatsAppGlyph className="size-4 shrink-0" />
          <span>Bagikan ke WhatsApp</span>
        </a>
      </div>
    </Card>
  );
}
